import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { LocatedCourier, courierName } from "@/features/courier-map/type.ts";
import { formatDateTime } from "@/shared/helper/datetime.ts";
import { locationFreshness } from "@/features/courier-map/freshness.ts";

// Алматы — центр по умолчанию, пока не пришло ни одной координаты.
const DEFAULT_CENTER: L.LatLngTuple = [43.238949, 76.889709];
const DEFAULT_ZOOM = 11;

const MARKER_COLORS: Record<string, string> = {
  fresh: "#16a34a",
  stale: "#f59e0b",
  lost: "#6b7280",
};

function initials(courier: LocatedCourier) {
  const source = courierName(courier);

  return source
    .split(" ")
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function markerIcon(courier: LocatedCourier, isSelected: boolean) {
  const color = MARKER_COLORS[locationFreshness(courier.updatedAt).level];
  const size = isSelected ? 40 : 32;

  return L.divIcon({
    className: "",
    html: `<div style="
        width:${size}px;height:${size}px;border-radius:9999px;
        background:${color};color:#fff;
        display:flex;align-items:center;justify-content:center;
        font:600 ${isSelected ? 14 : 12}px/1 system-ui,sans-serif;
        border:${isSelected ? 3 : 2}px solid #fff;
        box-shadow:0 1px 4px rgba(0,0,0,.4);
      ">${escapeHtml(initials(courier))}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

// Имя и телефон приходят из профиля пользователя, поэтому в разметку попапа
// попадают только экранированными.
function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function popupHtml(courier: LocatedCourier) {
  const freshness = locationFreshness(courier.updatedAt);

  return `
    <div style="min-width:180px">
      <div style="font-weight:600;margin-bottom:4px">${escapeHtml(courierName(courier))}</div>
      <div>${escapeHtml(courier.phoneNumber ?? "—")}</div>
      <div style="color:#6b7280;margin-top:4px">Обновлено: ${formatDateTime(courier.updatedAt)}</div>
      <div style="color:#6b7280">${freshness.label}</div>
    </div>
  `;
}

type Props = {
  couriers: LocatedCourier[];
  /** Курьер, на котором держим фокус: маркер увеличивается, карта центрируется. */
  selectedId?: string | null;
  onSelect?: (userId: string) => void;
};

/**
 * Карта на Leaflet + тайлы OpenStreetMap (без ключей и лимитов на регистрацию).
 * Инстанс карты живёт всё время жизни компонента, при обновлении данных
 * пересобираются только маркеры — иначе карта дёргалась бы каждые 20 секунд.
 */
export function CouriersMap({ couriers, selectedId, onSelect }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());
  const didFitRef = useRef(false);
  // Обработчик клика вешается на маркер один раз, поэтому держим его в ref —
  // иначе пришлось бы пересоздавать маркеры при каждом рендере страницы.
  const onSelectRef = useRef(onSelect);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "© OpenStreetMap",
    }).addTo(map);

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current.clear();
      didFitRef.current = false;
    };
  }, []);

  // Синхронизация маркеров с данными: добавляем новых, двигаем существующих,
  // убираем ушедших со смены.
  useEffect(() => {
    const map = mapRef.current;

    if (!map) return;

    const markers = markersRef.current;
    const seen = new Set<string>();

    for (const courier of couriers) {
      seen.add(courier.userId);

      const position: L.LatLngTuple = [courier.latitude, courier.longitude];
      const icon = markerIcon(courier, courier.userId === selectedId);
      const existing = markers.get(courier.userId);

      if (existing) {
        existing.setLatLng(position);
        existing.setIcon(icon);
        existing.setPopupContent(popupHtml(courier));

        continue;
      }

      const marker = L.marker(position, { icon })
        .addTo(map)
        .bindPopup(popupHtml(courier));

      marker.on("click", () => onSelectRef.current?.(courier.userId));
      markers.set(courier.userId, marker);
    }

    for (const [userId, marker] of markers) {
      if (seen.has(userId)) continue;

      marker.remove();
      markers.delete(userId);
    }

    // Подгоняем масштаб только один раз — дальше карта в руках оператора.
    if (!didFitRef.current && couriers.length > 0) {
      map.fitBounds(
        L.latLngBounds(
          couriers.map(
            (courier) => [courier.latitude, courier.longitude] as L.LatLngTuple,
          ),
        ),
        { padding: [48, 48], maxZoom: 15 },
      );
      didFitRef.current = true;
    }
  }, [couriers, selectedId]);

  // Клик по строке в списке — центрируем карту и открываем попап.
  useEffect(() => {
    const map = mapRef.current;

    if (!map || !selectedId) return;

    const marker = markersRef.current.get(selectedId);

    if (!marker) return;

    map.setView(marker.getLatLng(), Math.max(map.getZoom(), 14));
    marker.openPopup();
  }, [selectedId]);

  return <div ref={containerRef} className="h-full w-full rounded-lg z-0" />;
}
